// Loader singleton de Google Maps JavaScript API (libería Places).
//
// - cargarGoogleMaps() inyecta el <script> UNA sola vez y devuelve una promesa
//   cacheada que resuelve a window.google.maps (o null si no hay key / falla).
// - Si no hay VITE_GOOGLE_MAPS_API_KEY → resuelve null (el componente cae a un
//   input normal, sin romper).
// - gm_authFailure (key inválida / referrer / billing) marca un flag para que
//   los componentes degraden a input normal sin spamear la consola.

const KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''

let promesa = null
let authFallo = false

// Google llama a esta función global si la key es inválida / referrer mal /
// billing apagado. La definimos una vez; marca el flag para degradar.
if (typeof window !== 'undefined') {
  const previo = window.gm_authFailure
  window.gm_authFailure = () => {
    authFallo = true
    if (typeof previo === 'function') {
      try { previo() } catch { /* noop */ }
    }
  }
}

// ¿Google quedó inutilizable por fallo de auth? (lo consultan los componentes).
export const googleMapsAuthFallo = () => authFallo

export function cargarGoogleMaps() {
  // Sin key → no intentamos cargar; el componente usa input normal.
  if (!KEY) return Promise.resolve(null)

  // Ya cargado (o cargándose) → reusamos la misma promesa.
  if (promesa) return promesa

  promesa = new Promise((resolve) => {
    // Por si el script ya estaba en el DOM (HMR / doble montaje).
    if (window.google?.maps?.places) {
      resolve(window.google.maps)
      return
    }

    // Google avisa por CALLBACK cuando la API y las librerías pedidas están
    // listas. Preguntar por `google.maps.places` dentro de onload no sirve: el
    // bootstrap carga sus librerías DESPUÉS de ese evento, así que casi siempre
    // daba falso y el campo terminaba siendo un input común sin sugerencias.
    const CB = '__lgrMapsListo'
    window[CB] = () => {
      resolve(authFallo || !window.google?.maps?.places ? null : window.google.maps)
    }

    const script = document.createElement('script')
    const params = new URLSearchParams({
      key: KEY,
      libraries: 'places',
      language: 'es',
      region: 'AR',
      loading: 'async',
      callback: CB,
    })
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`
    script.async = true
    // Red de seguridad: si el callback no llega, el campo sigue andando como
    // input normal en vez de quedar esperando para siempre.
    setTimeout(() => resolve(window.google?.maps?.places ? window.google.maps : null), 10_000)
    script.onerror = () => {
      // Falló la red / bloqueo del script → degradamos a input normal.
      promesa = null // permitir reintento en una próxima monta
      resolve(null)
    }
    document.head.appendChild(script)
  })

  return promesa
}
